import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../styles/SwipeCheck.css";
import ElegantSidebar from "../../../shared/components/layout/ElegantSidebar";
import LazyImage from "../../../shared/components/ui/media/LazyImage";
import { SwipeImageData } from "../../../shared/types/gallery";
import { useImageUpdate } from "../../../contexts/ImageUpdateContext";
import { getBaseUrl } from "../../../config/urls";

import { getApiConfig } from "../../../shared/config/apiConfig";
import {
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Heart,
  ArrowLeft,
  Menu,
  X
} from "lucide-react";

// 拡張されたSwipeImageData型
interface ExtendedSwipeImageData extends SwipeImageData {
  imageUrl?: string;
}

const SwipeCheck: React.FC = () => {
  const navigate = useNavigate();
  const [images, setImages] = useState<ExtendedSwipeImageData[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  // サイドバーのレスポンシブ状態管理
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  // 画面サイズ別の表示制御
  const [screenSize, setScreenSize] = useState<'desktop' | 'tablet' | 'mobile-portrait' | 'mobile-landscape'>('desktop');

  // 動的な画像コンテナサイズの管理
  const [imageContainerHeight, setImageContainerHeight] = useState<number>(600);

  // 画面サイズを判定する関数
  const getScreenSize = (width: number, height: number): typeof screenSize => {
    if (width >= 1024) return 'desktop';
    if (width >= 768) return 'tablet';
    if (width > height) return 'mobile-landscape'; // 横向き
    return 'mobile-portrait'; // 縦向き
  };

  // スワイプアニメーション状態管理
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const roomId = useMemo(() => sessionStorage.getItem("roomid") || "", []);
  const urlBase = getBaseUrl();
  const { notifyImageUpdate } = useImageUpdate();

  // API設定（段階的移行用）
  const apiConfig = getApiConfig();
  const useLightApi = apiConfig.useLightApi;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const getThreshold = () => {
    const el = containerRef.current;
    const w = el ? el.clientWidth : window.innerWidth;
    return Math.max(50, Math.floor(w * 0.12)); // 幅の12%か50pxの大きい方
  };

  // 動的に画像コンテナの高さを計算する関数
  const calculateImageContainerHeight = useCallback(() => {
    if (!contentRef.current) return 600;

    const content = contentRef.current;
    const contentHeight = content.clientHeight;
    const contentRect = content.getBoundingClientRect();

    // ヘッダー、ステータス、その他の固定要素の高さを計算
    let fixedElementsHeight = 0;

    // ヘッダーセクション
    const header = content.querySelector('.swipe-header');
    if (header) fixedElementsHeight += header.getBoundingClientRect().height;

    // ステータスカードまたはコンパクトステータス
    const statusCard = content.querySelector('.swipe-status-card, .swipe-status-compact');
    if (statusCard) fixedElementsHeight += statusCard.getBoundingClientRect().height;

    // 画像インデックス表示
    const indexDisplay = content.querySelector('.image-index-display');
    if (indexDisplay) fixedElementsHeight += indexDisplay.getBoundingClientRect().height;

    // アクションメッセージエリア（潜在的な高さ）
    fixedElementsHeight += 80; // アクションメッセージ用の余白

    // パディングとマージンを考慮
    const padding = screenSize === 'mobile-landscape' ? 16 :
                   screenSize === 'mobile-portrait' ? 24 :
                   screenSize === 'tablet' ? 32 : 48;

    // 利用可能な高さを計算
    const availableHeight = Math.max(300, contentHeight - fixedElementsHeight - padding);

    return availableHeight;
  }, [screenSize]);

  // 画面サイズに応じてサイドバー状態を設定
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const newIsDesktop = width >= 1024;
      const newScreenSize = getScreenSize(width, height);

      setIsDesktop(newIsDesktop);
      setScreenSize(newScreenSize);

      // 画面サイズが変わった時のサイドバー状態の調整
      if (newIsDesktop && !isDesktop) {
        // モバイル → デスクトップ: サイドバーを閉じる（デスクトップでは常時表示）
        setSidebarOpen(false);
      } else if (!newIsDesktop && isDesktop) {
        // デスクトップ → モバイル: サイドバーを閉じる
        setSidebarOpen(false);
      }

      // 画像コンテナの高さを再計算（少し遅延させてDOM更新を待つ）
      setTimeout(() => {
        const newHeight = calculateImageContainerHeight();
        setImageContainerHeight(newHeight);
      }, 100);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isDesktop, calculateImageContainerHeight]);

  // SwipeCheck専用のLight API呼び出し（フック干渉回避）
  const swipeCheckAxiosRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  // 高速な画像取得関数（未検閲画像のみ）- 軽量版API対応
  // 実行中フラグでAPI重複実行を防止
  const fetchingRef = useRef(false);

  const fetchUnmoderatedImages = useCallback(async (force = false) => {
    // 既に実行中で強制実行でない場合はスキップ
    if (fetchingRef.current && !force) {
      return;
    }

    fetchingRef.current = true;

    try {
      if (useLightApi) {
        // 新しいAbortControllerを作成
        const currentController = new AbortController();
        swipeCheckAxiosRef.current = currentController;

        const token = localStorage.getItem("token");
        const requestUrl = `${urlBase}/api/picture/fetchAllImagesLight?roomId=${roomId}&page=0&size=100&moderationFilter=unmoderated&sort=recent`;

        const response = await axios.get(requestUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal: currentController.signal
        });

        // コンポーネントがまだマウントされている場合のみ状態更新
        if (isMountedRef.current && response.data && response.data.images) {
          const fetched: ExtendedSwipeImageData[] = response.data.images.map((img: any) => {
            // 相対URLを完全なURLに変換
            let fullImageUrl = img.imageUrl;
            if (fullImageUrl && fullImageUrl.startsWith('/')) {
              fullImageUrl = `${urlBase}${fullImageUrl}`;
            }

            return {
              id: img.pictureId,
              base64: '', // 軽量版では空文字
              imageUrl: fullImageUrl, // 完全なURLを使用
              isModerated: img.isModerated,
            };
          });

          setImages(fetched);
        }
      } else {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${urlBase}/api/picture/fetchAllImages?roomId=${roomId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const fetched: ExtendedSwipeImageData[] = res.data
          .filter((img: any) => !img.isModerated)
          .map((img: any) => ({
            id: img.pictureid,
            base64: img.base64,
            imageUrl: undefined,
            isModerated: img.isModerated,
          }));
        setImages(fetched);
      }
    } catch (fallbackError: any) {
      // AbortErrorの場合は正常なキャンセルとして処理
      if (fallbackError.name === 'AbortError' || fallbackError.name === 'CanceledError') {
        // 正常なキャンセル処理
      } else {
        console.error(`❌ 画像取得失敗:`, fallbackError);
      }
    } finally {
      fetchingRef.current = false;
    }
  }, [urlBase, roomId, useLightApi]);

  // 初回ロード用のuseEffect（無限ループ防止）
  const initialLoadRef = useRef(false);

  useEffect(() => {
    if (!initialLoadRef.current) {
      // React StrictMode対策：少し遅延させる
      const timer = setTimeout(() => {
        if (isMountedRef.current) {
          fetchUnmoderatedImages(true); // 強制実行フラグ
        }
      }, 100);
      initialLoadRef.current = true;

      return () => {
        clearTimeout(timer);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 依存配列を空にして初回のみ実行

  // 画像コンテナの初期サイズ計算とレイアウト更新の監視
  useEffect(() => {
    const updateImageContainerHeight = () => {
      const newHeight = calculateImageContainerHeight();
      setImageContainerHeight(newHeight);
    };

    // 初期計算
    const initialTimer = setTimeout(updateImageContainerHeight, 200);

    // ResizeObserverを使用してコンテンツサイズの変更を監視
    let resizeObserver: ResizeObserver | null = null;

    if (contentRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        updateImageContainerHeight();
      });
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      clearTimeout(initialTimer);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [calculateImageContainerHeight, screenSize, images.length]);

  // クリーンアップ: コンポーネント破棄時にリクエストをキャンセル
  useEffect(() => {
    isMountedRef.current = true; // マウント時にtrueに設定

    return () => {
      isMountedRef.current = false; // アンマウント時にfalseに設定
      initialLoadRef.current = false; // React StrictMode対策: 初回フラグもリセット

      if (swipeCheckAxiosRef.current) {
        swipeCheckAxiosRef.current.abort();
      }
    };
  }, []);

  const handlePrev = useCallback(() => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => Math.min(prev + 1, images.length - 1));
  }, [images.length]);

  const handleOk = useCallback(async () => {
    const currentImage = images[currentIndex];
    if (!currentImage) return;

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${urlBase}/api/moderation/update-ok`,
        [currentImage.id], // ← POSTのリクエストボディ
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json', // 配列なので明示しておくと安全
          },
        }
      );

      setImages((prevImages) => {
        const updated = prevImages.filter((img) => img.id !== currentImage.id);
        setCurrentIndex((idx) => Math.max(Math.min(idx, updated.length - 1), 0));
        return updated;
      });
      // 画像更新を通知
      notifyImageUpdate();

      // 成功メッセージ表示
      setActionMessage({ type: 'success', text: '✅ 承認しました' });
      setTimeout(() => setActionMessage(null), 2000);

      // DB反映を待機してから新しい未検閲画像をチェック（競合状態回避）
      setTimeout(() => {
        fetchUnmoderatedImages(true); // 強制実行
      }, 3000);
    } catch (error) {
      console.error(`❌ update-ok API失敗:`, error);

      // エラーメッセージ表示
      setActionMessage({ type: 'error', text: '❌ 承認に失敗しました' });
      setTimeout(() => setActionMessage(null), 3000);
    }
  }, [images, currentIndex, urlBase, notifyImageUpdate, fetchUnmoderatedImages]);

  const handleNg = useCallback(async () => {
    const currentImage = images[currentIndex];
    if (!currentImage) return;

    const apiStartTime = performance.now();

    try {
      const token = localStorage.getItem("token");
      await axios.post(`${urlBase}/api/moderation/update-ng`, [currentImage.id], {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      setImages((prevImages) => {
        const updated = prevImages.filter((img) => img.id !== currentImage.id);
        setCurrentIndex((idx) => Math.max(Math.min(idx, updated.length - 1), 0));
        return updated;
      });
      // 画像更新を通知
      notifyImageUpdate();

      // 成功メッセージ表示
      setActionMessage({ type: 'error', text: '❌ 拒否しました' });
      setTimeout(() => setActionMessage(null), 2000);

      // DB反映を待機してから新しい未検閲画像をチェック（競合状態回避）
      setTimeout(() => {
        fetchUnmoderatedImages(true); // 強制実行
      }, 3000);
    } catch (error) {
      console.error(`❌ update-ng API失敗:`, error);

      // エラーメッセージ表示
      setActionMessage({ type: 'error', text: '❌ 拒否に失敗しました' });
      setTimeout(() => setActionMessage(null), 3000);
    }
  }, [images, currentIndex, urlBase, notifyImageUpdate, fetchUnmoderatedImages]);

  // 従来のTouch/Mouseイベントは削除（Pointer Eventsに統一）

  // 軸ロック用(縦スクロールは無視)
  const pointerStartXRef = useRef<number | null>(null);
  const pointerStartYRef = useRef<number | null>(null);
  const isAxisLockedRef = useRef<"x" | "y" | null>(null);
  const pointerActiveRef = useRef<boolean>(false);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    // 左クリック/タッチ以外は無視（例えば右クリック）
    if (e.button !== 0 && e.pointerType === "mouse") return;

    pointerActiveRef.current = true;
    pointerStartXRef.current = e.clientX;
    pointerStartYRef.current = e.clientY;
    isAxisLockedRef.current = null;

    // 要素外でUPしても受け取れるようにキャプチャ
    const target = e.currentTarget as HTMLElement;
    if (target.setPointerCapture) {
      target.setPointerCapture(e.pointerId);
    }

    // 画像ドラッグやテキスト選択を抑止
    e.preventDefault();
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!pointerActiveRef.current || pointerStartXRef.current === null || pointerStartYRef.current === null) return;

    const dx = e.clientX - pointerStartXRef.current;
    const dy = e.clientY - pointerStartYRef.current;

    // 軸ロック判定（最初のわずかな移動で決め打ち）
    if (isAxisLockedRef.current === null) {
      if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
        isAxisLockedRef.current = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      }
    }

    // 縦方向にロックされたらスワイプ処理しない（スクロール優先）
    if (isAxisLockedRef.current === "y") return;

    // ドラッグオフセットを更新（リアルタイムアニメーション）
    const maxOffset = 150; // 最大ドラッグ距離
    const clampedOffset = Math.max(-maxOffset, Math.min(maxOffset, dx));
    setDragOffset(clampedOffset);

    // 横移動中はスクロール/選択を抑止
    e.preventDefault();
  }, []);

  const onPointerUp = useCallback(async (e: React.PointerEvent<HTMLDivElement>) => {
    if (!pointerActiveRef.current || pointerStartXRef.current === null) return;

    const dx = e.clientX - pointerStartXRef.current;
    const threshold = getThreshold();

    // リセット
    pointerActiveRef.current = false;
    pointerStartXRef.current = null;
    pointerStartYRef.current = null;
    isAxisLockedRef.current = null;

    // 縦スクロールの場合は処理しない
    if (isAxisLockedRef.current === "y") {
      setDragOffset(0); // ドラッグオフセットをリセット
      return;
    }

    if (dx > threshold) {
      // 右スワイプ → 承認アニメーション
      setSwipeDirection('right');
      setIsAnimating(true);
      setDragOffset(window.innerWidth); // 画面外まで移動
      setTimeout(async () => {
        await handleOk();
        setSwipeDirection(null);
        setIsAnimating(false);
        setDragOffset(0);
      }, 300);
    } else if (dx < -threshold) {
      // 左スワイプ → 拒否アニメーション
      setSwipeDirection('left');
      setIsAnimating(true);
      setDragOffset(-window.innerWidth); // 画面外まで移動
      setTimeout(async () => {
        await handleNg();
        setSwipeDirection(null);
        setIsAnimating(false);
        setDragOffset(0);
      }, 300);
    } else {
      // 閾値未満の場合は元に戻る
      setDragOffset(0);
    }
  }, [handleOk, handleNg]);

  const onPointerCancel = useCallback(() => {
    pointerActiveRef.current = false;
    pointerStartXRef.current = null;
    pointerStartYRef.current = null;
    isAxisLockedRef.current = null;
    setDragOffset(0); // ドラッグオフセットをリセット
  }, []);

  // MouseUpイベントも削除（Pointer Eventsに統一）

  // サイドバートグル関数
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // サイドバー外クリックで閉じる
  const handleSidebarOverlayClick = () => {
    if (!isDesktop) {
      setSidebarOpen(false);
    }
  };

  const currentImage = images[currentIndex];
  const prevImage = images[currentIndex - 1];
  const nextImage = images[currentIndex + 1];

  return (
    <div className="wedding-swipe-layout">
      {/* サイドバーオーバーレイ（モバイル時） */}
      {!isDesktop && sidebarOpen && (
        <div className="sidebar-overlay" onClick={handleSidebarOverlayClick}></div>
      )}

      {/* サイドバー */}
      <div className={`sidebar-container ${isDesktop ? 'desktop' : 'mobile'} ${sidebarOpen ? 'open' : ''}`}>
        <ElegantSidebar />
      </div>

      <div
        ref={contentRef}
        className={`wedding-swipe-content ${isDesktop ? 'desktop' : 'mobile'} screen-${screenSize}`}
      >
        {/* トグルボタン（モバイル時のみ） */}
        {!isDesktop && (
          <button className="sidebar-toggle-button" onClick={toggleSidebar}>
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        )}

        {/* ヘッダーセクション - モバイル横向きでは簡略化 */}
        {screenSize !== 'mobile-landscape' && (
          <div className="swipe-header">
            <div className="swipe-header-decoration">
            </div>
            <h1 className="swipe-title">
              スワイプ検閲
            </h1>
            {screenSize === 'desktop' && (
              <p className="swipe-subtitle">Wedding Photo Swipe Moderation</p>
            )}

          </div>
        )}

        {/* ステータス情報 - モバイル横向きではコンパクト化 */}
        {screenSize === 'mobile-landscape' ? (
          <div className="swipe-status-compact">
            <span className="status-compact-info">{images.length}枚</span>
            <button
              onClick={() => navigate(-1)}
              className="elegant-back-button-compact"
              title="戻る"
            >
              <ArrowLeft size={18} />
            </button>
          </div>
        ) : (
          <div className="swipe-status-card">
            <div className="status-info">
              <div className="status-icon">
                <Heart size={24} />
              </div>
              <div className="status-content">
                <div className="status-number">{images.length}</div>
                <div className="status-label">未検閲写真</div>
              </div>
            </div>

            <button
              onClick={() => navigate(-1)}
              className="elegant-back-button"
            >
              <ArrowLeft size={20} />
              戻る
            </button>
          </div>
        )}

        {/* 現在の画像インデックス表示 - モバイルではオーバーレイ化 */}
        {images.length > 0 && screenSize !== 'mobile-landscape' && screenSize !== 'mobile-portrait' && (
          <div className="image-index-display">
            <span className="current-image-index">{currentIndex + 1}</span>
            <span className="index-separator"> / </span>
            <span className="total-image-count">{images.length}</span>
          </div>
        )}

        {/* メイン画像表示エリア */}
        <div className="elegant-image-container">
          <div
            ref={containerRef}
            className="elegant-image-preview"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            style={{
              transform: `translateX(${dragOffset}px) rotate(${dragOffset * 0.1}deg)`,
              transition: isAnimating ? 'transform 0.3s ease-out' : 'none',
              opacity: Math.abs(dragOffset) > 100 ? Math.max(0.3, 1 - Math.abs(dragOffset) / 300) : 1,
              height: `${imageContainerHeight}px`,
              maxHeight: 'none'
            }}
          >
            {currentImage ? (
              <>
                <LazyImage
                  src={currentImage.imageUrl && currentImage.imageUrl.trim() ?
                    currentImage.imageUrl :
                    (currentImage.base64 && currentImage.base64.trim() ?
                      `data:image/png;base64,${currentImage.base64}` :
                      'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjVmNWY1Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZvbnQtZmFtaWx5PSJzYW5zLXNlcmlmIiBmb250LXNpemU9IjE4cHgiIGZpbGw9IiM5OTkiPueUgeWDj+OBhOOBquOBmTwvdGV4dD48L3N2Zz4=')}
                  alt="current"
                  className="elegant-preview-image"
                  placeholder="🖼️"
                  style={{
                    objectFit: 'contain',
                    objectPosition: 'center',
                    width: '100%',
                    height: '100%'
                  }}
                />
                {/* スワイプ方向インジケーター */}
                {Math.abs(dragOffset) > 50 && (
                  <div className={`swipe-indicator ${dragOffset > 0 ? 'approve' : 'reject'}`}>
                    {dragOffset > 0 ? (
                      <>
                        <CheckCircle size={48} />
                        <span>承認</span>
                      </>
                    ) : (
                      <>
                        <XCircle size={48} />
                        <span>拒否</span>
                      </>
                    )}
                  </div>
                )}
                {/* スワイプヒント（ドラッグ中は非表示） */}
                {Math.abs(dragOffset) <= 10 && (
                  <div className="swipe-hints">
                    <div className="swipe-hint swipe-hint-left">
                      <XCircle size={screenSize.includes('mobile') ? 20 : 24} />
                      <span>左スワイプで拒否</span>
                    </div>
                    <div className="swipe-hint swipe-hint-right">
                      <CheckCircle size={screenSize.includes('mobile') ? 20 : 24} />
                      <span>右スワイプで承認</span>
                    </div>
                  </div>
                )}

                {/* モバイル用オーバーレイ情報 */}
                {images.length > 0 && screenSize.includes('mobile') && Math.abs(dragOffset) <= 10 && (
                  <div className="mobile-overlay-info">
                    <div className="mobile-index-display">
                      {currentIndex + 1} / {images.length}
                    </div>
                    {screenSize === 'mobile-landscape' && (
                      <div className="mobile-title">スワイプ検閲</div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="elegant-no-image">
                <Heart size={48} />
                <p>検閲待ちの写真がありません</p>
                <p className="no-image-subtitle">すべての写真が処理済みです</p>
              </div>
            )}
          </div>

          {/* ナビゲーション矢印 - モバイルではサイズ調整 */}
          {images.length > 1 && (
            <>
              <button
                className={`nav-arrow nav-prev ${!prevImage ? 'disabled' : ''} ${screenSize}`}
                onClick={handlePrev}
                disabled={!prevImage}
                title="前の画像"
              >
                <ChevronLeft size={screenSize.includes('mobile') ? 24 : 28} />
              </button>
              <button
                className={`nav-arrow nav-next ${!nextImage ? 'disabled' : ''} ${screenSize}`}
                onClick={handleNext}
                disabled={!nextImage}
                title="次の画像"
              >
                <ChevronRight size={screenSize.includes('mobile') ? 24 : 28} />
              </button>
            </>
          )}
        </div>



        {/* アクションメッセージ */}
        {actionMessage && (
          <div className={`action-message ${actionMessage.type}`}>
            {actionMessage.text}
          </div>
        )}

      </div>
    </div>
  );
};

export default SwipeCheck;
